import React, { useEffect, useState } from 'react';
import { Coins, Shuffle, Save } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { money } from '../lib/constants';
import { PageHeader, Card } from './shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const AdminDispatch = () => {
  const { driverPay, autoAssign, saveDriverPay, saveAutoAssign, drivers } = useApp();
  const [pay, setPay] = useState({ enabled: false, perOrderFee: 55 });
  const [auto, setAuto] = useState({ enabled: false, timeoutMinutes: 5 });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (driverPay) setPay(driverPay); }, [driverPay]);
  useEffect(() => { if (autoAssign) setAuto(autoAssign); }, [autoAssign]);
  const activeDrivers = drivers.filter((d) => d.approval === 'approved' && d.status === 'active' && d.available).length;

  const savePay = async () => {
    setBusy(true);
    try { await saveDriverPay({ enabled: pay.enabled, perOrderFee: Number(pay.perOrderFee) || 0 }); } catch { /* toast */ } finally { setBusy(false); }
  };
  const saveAuto = async () => {
    setBusy(true);
    try { await saveAutoAssign({ enabled: auto.enabled, timeoutMinutes: Number(auto.timeoutMinutes) || 5 }); } catch { /* toast */ } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Pago y asignación" subtitle="Define cuánto se le paga al conductor por pedido y si los pedidos se asignan automáticamente" />
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card title="Pago al conductor por pedido">
          <div className="p-5 pt-0 space-y-4">
            <div className="flex items-center justify-between rounded-xl bg-[#F7F8FA] p-3.5">
              <div className="min-w-0 pr-3">
                <p className="text-sm font-semibold">Pago automático fijo</p>
                <p className="text-xs text-gray-500">Si está activo, todo pedido a domicilio nace con este pago. Si está apagado se usa el cálculo anterior (envío + L30).</p>
              </div>
              <Switch data-testid="driver-pay-enabled" checked={!!pay.enabled} onCheckedChange={(v) => setPay({ ...pay, enabled: v })} className="data-[state=checked]:bg-emerald-500" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-gray-500">Pago por pedido (L)</Label>
              <Input data-testid="driver-pay-fee" type="number" min="0" step="1" value={pay.perOrderFee} onChange={(e) => setPay({ ...pay, perOrderFee: e.target.value })} className="mt-1 max-w-[200px]" />
              <p className="text-xs text-gray-500 mt-1.5">Ejemplo: si al cliente le cobras L70 de envío y aquí pones {money(Number(pay.perOrderFee) || 0)}, el conductor recibe {money(Number(pay.perOrderFee) || 0)} y el resto queda para la plataforma.</p>
            </div>
            <p className="text-xs text-gray-500 flex items-start gap-2"><Coins size={14} className="mt-0.5 shrink-0" /> También puedes asignar el pago manualmente en cada pedido desde <span className="font-semibold">Pedidos → Ver → Pago al conductor</span>. El monto manual siempre gana sobre el automático.</p>
            <Button data-testid="driver-pay-save" disabled={busy} onClick={savePay} className="bg-[#EE3B36] hover:bg-[#D93129] gap-2"><Save size={16} /> Guardar pago</Button>
          </div>
        </Card>

        <Card title="Asignación automática de pedidos">
          <div className="p-5 pt-0 space-y-4">
            <div className="flex items-center justify-between rounded-xl bg-[#F7F8FA] p-3.5">
              <div className="min-w-0 pr-3">
                <p className="text-sm font-semibold">Asignar pedidos automáticamente</p>
                <p className="text-xs text-gray-500">Cada pedido nuevo se asigna al azar a un conductor activo y disponible. Si no lo acepta en el tiempo límite, se marca como rechazado para él y pasa al azar a otro conductor.</p>
              </div>
              <Switch data-testid="auto-assign-enabled" checked={!!auto.enabled} onCheckedChange={(v) => setAuto({ ...auto, enabled: v })} className="data-[state=checked]:bg-emerald-500" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-gray-500">Tiempo para aceptar</Label>
              <Select value={String(auto.timeoutMinutes)} onValueChange={(v) => setAuto({ ...auto, timeoutMinutes: Number(v) })}>
                <SelectTrigger data-testid="auto-assign-timeout" className="mt-1 max-w-[200px]"><SelectValue /></SelectTrigger>
                <SelectContent>{[2, 3, 5, 10, 15].map((m) => <SelectItem key={m} value={String(m)}>{m} minutos</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <p data-testid="auto-assign-drivers" className="text-xs text-gray-500 flex items-start gap-2"><Shuffle size={14} className="mt-0.5 shrink-0" /> Conductores activos y disponibles ahora: <span className="font-bold text-[#1E2130]">{activeDrivers}</span>. Si ninguno acepta, el pedido queda visible para todos y recibirás una notificación.</p>
            <Button data-testid="auto-assign-save" disabled={busy} onClick={saveAuto} className="bg-[#EE3B36] hover:bg-[#D93129] gap-2"><Save size={16} /> Guardar asignación</Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AdminDispatch;
